import { useCallback, useEffect, useRef, useState } from "react";
import type {
  AnswerFeedback,
  LeaderboardView,
  PublicQuestion,
  RaceProgress,
  RaceResult,
} from "@shared/types";
import { formatMs, trackFraction } from "@shared/scoring";
import { bunnyFraction, bunnyPaceMs, type BestPace } from "@shared/pace";
import {
  recordRaceStart,
  startDaily,
  startSolo,
  submitAnswer,
  type SoloConfig,
} from "../lib/api";
import { getActiveProfile, type LocalProfile } from "../lib/profile";
import { getPersonalBest, recordPersonalBest } from "../lib/personalBest";
import { sfx } from "../lib/sound";
import Countdown from "../components/Countdown";
import QuestionCard from "../components/QuestionCard";
import RaceTrack, { type Lane } from "../components/RaceTrack";
import Results from "../components/Results";
import SceneBackground from "../components/brand/SceneBackground";
import Wordmark from "../components/brand/Wordmark";

type Phase = "loading" | "countdown" | "question" | "feedback" | "results" | "error";

const FEEDBACK_MS = 1700;

interface Pending {
  finished: boolean;
  token?: string;
  question?: PublicQuestion;
  result?: RaceResult;
  resultToken?: string;
}

export default function SoloRace({
  config,
  daily = false,
  onHome,
  onViewLeaderboard,
  onViewProfile,
}: {
  config: SoloConfig;
  /** Run today's Daily Challenge instead of a configured solo race. */
  daily?: boolean;
  onHome: () => void;
  onViewLeaderboard?: (board: LeaderboardView) => void;
  onViewProfile?: (profile: LocalProfile) => void;
}) {
  const [phase, setPhase] = useState<Phase>("loading");
  const [errorMsg, setErrorMsg] = useState("");
  const [token, setToken] = useState("");
  const [question, setQuestion] = useState<PublicQuestion | null>(null);
  const [progress, setProgress] = useState<RaceProgress>({
    correctCount: 0,
    attempts: 0,
    finishLine: config.finishLine,
  });
  const [feedback, setFeedback] = useState<AnswerFeedback | null>(null);
  const [selectedAnswer, setSelectedAnswer] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<RaceResult | null>(null);
  const [resultToken, setResultToken] = useState<string | undefined>(undefined);
  const [now, setNow] = useState(0);
  // The player's best pace for this race type — the Pace Bunny chases it.
  const [best, setBest] = useState<BestPace | null>(null);
  // Whether the just-finished run set a new record (beat a previous best).
  const [newBest, setNewBest] = useState(false);

  const pending = useRef<Pending | null>(null);
  const questionStart = useRef(0);
  const raceStart = useRef(0);
  const advanceTimer = useRef<number | null>(null);

  // Personal-best bucket: per active profile + race type (difficulty, or today's
  // Daily). Different kids on a shared device — and different difficulties —
  // chase their own best, not each other's.
  const paceBucket = useCallback(() => {
    const pid = getActiveProfile()?.localId ?? "anon";
    return daily
      ? `${pid}:daily:${new Date().toISOString().slice(0, 10)}`
      : `${pid}:solo:${config.difficulty}`;
  }, [daily, config.difficulty]);

  // Kick off the race.
  useEffect(() => {
    let alive = true;
    setPhase("loading");
    (daily ? startDaily() : startSolo(config))
      .then((res) => {
        if (!alive) return;
        // Count the start only once the server has actually handed back a race,
        // so failed starts don't inflate the funnel's denominator.
        recordRaceStart(daily ? "daily" : "solo");
        setToken(res.token);
        setQuestion(res.question);
        setProgress(res.progress);
        setBest(getPersonalBest(paceBucket()));
        setNewBest(false);
        setPhase("countdown");
      })
      .catch((err: unknown) => {
        if (!alive) return;
        setErrorMsg(err instanceof Error ? err.message : "Could not start race.");
        setPhase("error");
      });
    return () => {
      alive = false;
    };
  }, [config, daily, paceBucket]);

  // Clock for the per-question timer + pace ghost (runs while playing).
  useEffect(() => {
    if (phase !== "question" && phase !== "feedback") return;
    const id = window.setInterval(() => setNow(performance.now()), 100);
    return () => window.clearInterval(id);
  }, [phase]);

  const startQuestionClock = useCallback(() => {
    const t = performance.now();
    questionStart.current = t;
    if (raceStart.current === 0) raceStart.current = t;
    setNow(t);
  }, []);

  const onCountdownDone = useCallback(() => {
    startQuestionClock();
    setPhase("question");
  }, [startQuestionClock]);

  const goNext = useCallback(() => {
    if (advanceTimer.current) {
      window.clearTimeout(advanceTimer.current);
      advanceTimer.current = null;
    }
    const next = pending.current;
    if (!next) return;
    pending.current = null;

    if (next.finished && next.result) {
      sfx.finish();
      const hadBest = best !== null;
      const { improved } = recordPersonalBest(
        paceBucket(),
        next.result.totalMs,
        next.result.finishLine,
      );
      // Celebrate only when beating a *previous* best — not the first finish.
      setNewBest(improved && hadBest);
      setResult(next.result);
      setResultToken(next.resultToken);
      setPhase("results");
      return;
    }
    if (next.token && next.question) {
      setToken(next.token);
      setQuestion(next.question);
      setFeedback(null);
      setSelectedAnswer(null);
      startQuestionClock();
      setPhase("question");
    }
  }, [startQuestionClock, paceBucket, best]);

  const handleSubmit = useCallback(
    async (answer: string) => {
      if (submitting || phase !== "question") return;
      setSubmitting(true);
      setSelectedAnswer(answer);
      try {
        const res = await submitAnswer(token, answer);
        if (res.feedback.correct) sfx.correct();
        else sfx.wrong();

        setFeedback(res.feedback);
        setProgress(res.progress);
        pending.current = {
          finished: res.finished,
          token: res.token,
          question: res.question,
          result: res.result,
          resultToken: res.resultToken,
        };
        setPhase("feedback");
        // Auto-advance after a beat; player can also press Next / Space.
        advanceTimer.current = window.setTimeout(goNext, FEEDBACK_MS);
      } catch (err) {
        setErrorMsg(err instanceof Error ? err.message : "Submission failed.");
        setPhase("error");
      } finally {
        setSubmitting(false);
      }
    },
    [submitting, phase, token, goNext],
  );

  useEffect(() => {
    return () => {
      if (advanceTimer.current) window.clearTimeout(advanceTimer.current);
    };
  }, []);

  if (phase === "loading") {
    return (
      <div className="relative min-h-full">
        <SceneBackground />
        <div className="flex min-h-[70vh] flex-col items-center justify-center gap-4">
          <Wordmark width={300} />
          <div className="panel px-6 py-4 font-display text-xl text-ink">
            Revving up your race…
          </div>
        </div>
      </div>
    );
  }

  if (phase === "error") {
    return (
      <div className="relative min-h-full">
        <SceneBackground />
        <div className="mx-auto flex min-h-[70vh] max-w-md flex-col items-center justify-center px-4 text-center">
          <div className="panel w-full p-7">
            <div className="mb-3 text-5xl">😕</div>
            <p className="mb-5 font-semibold text-ink/80">{errorMsg}</p>
            <button className="btn-play w-full" onClick={onHome}>
              <span aria-hidden>↩</span> BACK TO START
              <span className="sheen animate-shine" aria-hidden />
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (phase === "results" && result) {
    return (
      <div className="relative min-h-full">
        <SceneBackground />
        <Results
          result={result}
          resultToken={resultToken}
          mode={daily ? "daily" : "solo"}
          newBest={newBest}
          onViewLeaderboard={onViewLeaderboard}
          onViewProfile={onViewProfile}
          onHome={onHome}
          onPlayAgain={() => {
            // Reset and restart the same race (same config, or the same Daily).
            raceStart.current = 0;
            questionStart.current = 0;
            pending.current = null;
            setResult(null);
            setResultToken(undefined);
            setFeedback(null);
            setSelectedAnswer(null);
            setProgress({
              correctCount: 0,
              attempts: 0,
              finishLine: config.finishLine,
            });
            setPhase("loading");
            (daily ? startDaily() : startSolo(config))
              .then((res) => {
                recordRaceStart(daily ? "daily" : "solo");
                setToken(res.token);
                setQuestion(res.question);
                setProgress(res.progress);
                setBest(getPersonalBest(paceBucket()));
                setNewBest(false);
                setPhase("countdown");
              })
              .catch((err: unknown) => {
                setErrorMsg(
                  err instanceof Error ? err.message : "Could not start race.",
                );
                setPhase("error");
              });
          }}
        />
      </div>
    );
  }

  const finishLine = progress.finishLine;
  const youFraction = trackFraction(progress.correctCount, finishLine);
  const bunnyFrac = bunnyFraction(
    raceStart.current,
    now,
    finishLine,
    bunnyPaceMs(best),
  );

  const lanes: Lane[] = [
    {
      id: "you",
      label: "You",
      fraction: youFraction,
      carColor: "#ff5a4d",
      number: 1,
      isYou: true,
      finished: youFraction >= 1,
    },
    {
      id: "pace",
      label: best ? "Your best" : "Pace Bunny",
      fraction: bunnyFrac,
      carColor: "#9aa3b2",
      number: "P",
      emoji: "🐰",
      isGhost: true,
      finished: bunnyFrac >= 1,
    },
  ];

  const questionElapsed =
    phase === "question" && questionStart.current > 0
      ? Math.max(0, now - questionStart.current)
      : 0;

  return (
    <div className="relative min-h-full">
      <SceneBackground showSpeed={phase === "question"} />

      <main className="mx-auto w-full max-w-2xl px-4 pb-12 pt-4">
        <div className="mb-4 flex items-center justify-between gap-2">
          <button
            type="button"
            className="btn-chunk bg-white px-4 py-2 text-sm"
            onClick={onHome}
          >
            ← Quit
          </button>
          <Wordmark width={150} className="hidden shrink-0 sm:block" />
          <div className="flex items-center gap-2">
            <span className="coin-badge px-2.5 py-1 text-sm tabular-nums">
              <span aria-hidden>✓</span>
              {progress.correctCount}/{finishLine}
            </span>
            <span className="coin-badge px-2.5 py-1 text-sm tabular-nums text-ink/70">
              {formatMs(questionElapsed)}
            </span>
          </div>
        </div>

        <div className="mb-5">
          <RaceTrack lanes={lanes} />
        </div>

        {phase === "countdown" ? (
          <Countdown onDone={onCountdownDone} />
        ) : question ? (
          <QuestionCard
            question={question}
            feedback={feedback}
            selectedAnswer={selectedAnswer}
            submitting={submitting}
            onSubmit={handleSubmit}
            onNext={goNext}
          />
        ) : null}
      </main>
    </div>
  );
}
